import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  CommunityAttachment,
  CommunityAttachmentRequest,
  CommunityAttachmentRules,
  CommunityCategory,
  CommunityDiscussion,
  CommunityDiscussionDetail,
  CommunityDiscussionInput,
  CommunityReaction,
  CommunityReply,
  CommunitySpace,
  CommunityTag,
  PageResponse,
} from '../models';

/** DiscussionResponse brut côté backend : seuls les champs utilisés côté frontend sont typés ici, tagsCsv arrive déjà éclaté en tableau (voir CommunityForumResponses.DiscussionResponse.tags). */
interface RawDiscussion extends Omit<CommunityDiscussion, 'tags'> {
  tags: string[] | null;
}

function toDiscussion(raw: RawDiscussion): CommunityDiscussion {
  return { ...raw, tags: raw.tags ?? [] };
}

/**
 * UC-16/17 "Communautés" : forum professionnel réel (communication-service,
 * CommunityForumController, /api/v1/communications/community/**). Espaces = communautés
 * thématiques, discussions = sujets, réponses = le "chat" de chaque communauté. "Rejoindre" une
 * communauté = suivre un espace (follow), "participer aux échanges" = créer une discussion,
 * répondre, réagir. Tout exige un JWT — pas de lecture anonyme (SecurityConfig).
 *
 * Périmètre volontairement réduit aux actions jeune/membre : mentions, présence/typing,
 * statistiques et modération (/admin/**) ne sont pas couvertes ici — ce sont des fonctionnalités
 * back-office, pas un écran espace jeune. Les pièces jointes (images/vidéos/GIF/documents) le sont
 * en revanche, ajoutées après le premier passage sur ces UC (voir attachmentRules() ci-dessous).
 */
@Injectable({ providedIn: 'root' })
export class CommunityForumService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/communication-service/api/v1/communications/community`;

  spaces(page = 0, size = 50): Observable<PageResponse<CommunitySpace>> {
    const params = new HttpParams().set('page', page).set('size', size);
    return this.http.get<PageResponse<CommunitySpace>>(`${this.baseUrl}/spaces`, { params });
  }

  space(spaceId: string): Observable<CommunitySpace> {
    return this.http.get<CommunitySpace>(`${this.baseUrl}/spaces/${spaceId}`);
  }

  followedSpaces(): Observable<CommunitySpace[]> {
    return this.http.get<CommunitySpace[]>(`${this.baseUrl}/me/followed-spaces`);
  }

  followSpace(spaceId: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/spaces/${spaceId}/follow`, {});
  }

  unfollowSpace(spaceId: string): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/spaces/${spaceId}/follow`);
  }

  categories(page = 0, size = 50): Observable<PageResponse<CommunityCategory>> {
    const params = new HttpParams().set('page', page).set('size', size);
    return this.http.get<PageResponse<CommunityCategory>>(`${this.baseUrl}/categories`, { params });
  }

  tags(): Observable<CommunityTag[]> {
    return this.http.get<CommunityTag[]>(`${this.baseUrl}/tags`);
  }

  discussions(
    filters: { spaceId?: string; query?: string; page?: number; size?: number } = {},
  ): Observable<PageResponse<CommunityDiscussion>> {
    let params = new HttpParams().set('page', filters.page ?? 0).set('size', filters.size ?? 20);
    if (filters.spaceId) params = params.set('spaceId', filters.spaceId);
    if (filters.query) params = params.set('query', filters.query);
    return this.http
      .get<PageResponse<RawDiscussion>>(`${this.baseUrl}/discussions`, { params })
      .pipe(map((res) => ({ ...res, content: res.content.map(toDiscussion) })));
  }

  trending(page = 0, size = 10): Observable<PageResponse<CommunityDiscussion>> {
    const params = new HttpParams().set('page', page).set('size', size);
    return this.http
      .get<PageResponse<RawDiscussion>>(`${this.baseUrl}/trending`, { params })
      .pipe(map((res) => ({ ...res, content: res.content.map(toDiscussion) })));
  }

  unanswered(page = 0, size = 10): Observable<PageResponse<CommunityDiscussion>> {
    const params = new HttpParams().set('page', page).set('size', size);
    return this.http
      .get<PageResponse<RawDiscussion>>(`${this.baseUrl}/unanswered`, { params })
      .pipe(map((res) => ({ ...res, content: res.content.map(toDiscussion) })));
  }

  discussion(discussionId: string): Observable<CommunityDiscussionDetail> {
    return this.http
      .get<Omit<CommunityDiscussionDetail, 'discussion'> & { discussion: RawDiscussion }>(
        `${this.baseUrl}/discussions/${discussionId}`,
      )
      .pipe(map((res) => ({ ...res, discussion: toDiscussion(res.discussion) })));
  }

  createDiscussion(input: CommunityDiscussionInput): Observable<CommunityDiscussion> {
    return this.http
      .post<RawDiscussion>(`${this.baseUrl}/discussions`, {
        spaceId: input.spaceId,
        categoryId: input.categoryId ?? null,
        title: input.title,
        body: input.body,
        tags: input.tags ?? [],
      })
      .pipe(map(toDiscussion));
  }

  /** Publie community.discussion-read côté backend et alimente /me/unread-count : à appeler à l'ouverture d'une discussion. */
  markRead(discussionId: string): Observable<unknown> {
    return this.http.patch(`${this.baseUrl}/discussions/${discussionId}/read`, {});
  }

  view(discussionId: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/discussions/${discussionId}/view`, {});
  }

  replies(discussionId: string, page = 0, size = 50): Observable<PageResponse<CommunityReply>> {
    const params = new HttpParams().set('page', page).set('size', size);
    return this.http.get<PageResponse<CommunityReply>>(`${this.baseUrl}/discussions/${discussionId}/replies`, { params });
  }

  createReply(discussionId: string, body: string, parentReplyId?: string): Observable<CommunityReply> {
    return this.http.post<CommunityReply>(`${this.baseUrl}/discussions/${discussionId}/replies`, {
      body,
      parentReplyId: parentReplyId ?? null,
    });
  }

  /** Réservé à l'auteur de la discussion (vérifié côté backend) : marque une réponse comme solution. */
  acceptReply(replyId: string): Observable<CommunityReply> {
    return this.http.post<CommunityReply>(`${this.baseUrl}/replies/${replyId}/accept`, {});
  }

  unacceptReply(replyId: string): Observable<CommunityReply> {
    return this.http.delete<CommunityReply>(`${this.baseUrl}/replies/${replyId}/accept`);
  }

  /** Mes réactions, tous types de cible confondus (pas de filtre serveur par targetId) — sert à savoir quelles réactions sont déjà les miennes sur une discussion/réponse donnée, voir shared/utils/community-forum.mapper.ts#myReactionTypes. */
  myReactions(page = 0, size = 200): Observable<PageResponse<CommunityReaction>> {
    const params = new HttpParams().set('page', page).set('size', size);
    return this.http.get<PageResponse<CommunityReaction>>(`${this.baseUrl}/me/reactions`, { params });
  }

  reactToDiscussion(discussionId: string, reactionType: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/discussions/${discussionId}/reactions`, { reactionType });
  }

  removeDiscussionReaction(discussionId: string, reactionType: string): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/discussions/${discussionId}/reactions/${reactionType}`);
  }

  reactToReply(replyId: string, reactionType: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/replies/${replyId}/reactions`, { reactionType });
  }

  removeReplyReaction(replyId: string, reactionType: string): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/replies/${replyId}/reactions/${reactionType}`);
  }

  followDiscussion(discussionId: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/discussions/${discussionId}/follow`, {});
  }

  unfollowDiscussion(discussionId: string): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/discussions/${discussionId}/follow`);
  }

  followedDiscussionIds(): Observable<string[]> {
    return this.http
      .get<RawDiscussion[]>(`${this.baseUrl}/me/followed-discussions`)
      .pipe(map((items) => items.map((item) => item.id)));
  }

  bookmark(discussionId: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/discussions/${discussionId}/bookmark`, {});
  }

  removeBookmark(discussionId: string): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/discussions/${discussionId}/bookmark`);
  }

  bookmarkedDiscussionIds(): Observable<string[]> {
    return this.http
      .get<{ discussionId: string }[]>(`${this.baseUrl}/me/bookmarks`)
      .pipe(map((items) => items.map((item) => item.discussionId)));
  }

  reportDiscussion(discussionId: string, reasonCode: string, description?: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/discussions/${discussionId}/report`, { reasonCode, description });
  }

  reportReply(replyId: string, reasonCode: string, description?: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/replies/${replyId}/report`, { reasonCode, description });
  }

  /** Extensions/types MIME/taille/nombre de fichiers autorisés — à charger avant d'afficher l'éditeur de discussion ou de réponse. */
  attachmentRules(): Observable<CommunityAttachmentRules> {
    return this.http.get<CommunityAttachmentRules>(`${this.baseUrl}/attachments/rules`);
  }

  addDiscussionAttachments(discussionId: string, requests: CommunityAttachmentRequest[]): Observable<CommunityAttachment[]> {
    return this.http.post<CommunityAttachment[]>(`${this.baseUrl}/discussions/${discussionId}/attachments`, requests);
  }

  discussionAttachments(discussionId: string): Observable<CommunityAttachment[]> {
    return this.http.get<CommunityAttachment[]>(`${this.baseUrl}/discussions/${discussionId}/attachments`);
  }

  /** Le fichier source n'est pas supprimé, seule l'association à la discussion l'est. */
  deleteDiscussionAttachment(discussionId: string, attachmentId: string): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/discussions/${discussionId}/attachments/${attachmentId}`);
  }

  addReplyAttachments(replyId: string, requests: CommunityAttachmentRequest[]): Observable<CommunityAttachment[]> {
    return this.http.post<CommunityAttachment[]>(`${this.baseUrl}/replies/${replyId}/attachments`, requests);
  }

  /** ReplyResponse n'embarque pas ses pièces jointes (contrairement à la discussion) : à appeler par réponse. */
  replyAttachments(replyId: string): Observable<CommunityAttachment[]> {
    return this.http.get<CommunityAttachment[]>(`${this.baseUrl}/replies/${replyId}/attachments`);
  }

  deleteReplyAttachment(replyId: string, attachmentId: string): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/replies/${replyId}/attachments/${attachmentId}`);
  }
}
